import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { FEATURE_TO_ROUTE_MAP } from '../feature-constants';
import {
  Feature,
  MenuItem,
  FeatureDetail,
  MENU_CONFIGURATION,
  FEATURE_DETAILS,
  COMPONENT_TO_FEATURE_MAP,
  FEATURE_TO_FEATURE_MAP,
  FEATURE_CONFIG
} from '../models/feature.model';

export interface FeatureConfig {
  menuConfiguration: {
    mainMenuItems: MenuItem[];
  };
  componentToFeatureMapping: Record<string, string>;
  featureToFeatureMapping: Record<string, string>;
  features: FeatureDetail[];
}

@Injectable({
  providedIn: 'root'
})
export class FeatureConfigService {
  private featureConfig: FeatureConfig | null = null;

  constructor() {
    this.loadFeatureConfig();
  }

  /**
   * Load feature configuration from TypeScript constants
   */
  loadFeatureConfig(): Observable<FeatureConfig> {
    if (this.featureConfig) {
      return of(this.featureConfig);
    }

    // Use TypeScript constants instead of JSON
    this.featureConfig = {
      menuConfiguration: {
        mainMenuItems: MENU_CONFIGURATION
      },
      componentToFeatureMapping: COMPONENT_TO_FEATURE_MAP,
      featureToFeatureMapping: FEATURE_TO_FEATURE_MAP,
      features: FEATURE_DETAILS
    };

    this.processMenuConfiguration();
    return of(this.featureConfig);
  }

  /**
   * Get processed menu items with routes
   */
  getMenuItems(): Observable<MenuItem[]> {
    return this.loadFeatureConfig().pipe(
      map(config => config.menuConfiguration.mainMenuItems)
    );
  }

  /**
   * Get feature configuration
   */
  getFeatureConfig(): Observable<FeatureConfig> {
    return this.loadFeatureConfig();
  }

  /**
   * Process menu configuration to add routes and handle special cases
   */
  private processMenuConfiguration(): void {
    if (!this.featureConfig) return;

    this.featureConfig.menuConfiguration.mainMenuItems.forEach(item => {
      // Add route for single items
      if (item.type === 'single') {
        item.route = FEATURE_TO_ROUTE_MAP[item.feature];
      }

      // Process children for group items
      if (item.type === 'group' && item.children) {
        item.children.forEach(child => {
          child.route = FEATURE_TO_ROUTE_MAP[child.feature as Feature];
        });
      }
    });
  }

  /**
   * Get component name for a feature
   */
  getComponentForFeature(feature: string): string | null {
    if (!this.featureConfig) return null;

    const featureEntry = this.featureConfig.features.find(f => f.feature === feature);
    return featureEntry ? featureEntry.component : null;
  }

  /**
   * Get path for a feature
   */
  getPathForFeature(feature: string): string | null {
    if (!this.featureConfig) return null;

    const featureEntry = this.featureConfig.features.find(f => f.feature === feature);
    return featureEntry ? featureEntry.path : null;
  }

  /**
   * Get all features
   */
  getAllFeatures(): FeatureDetail[] {
    return this.featureConfig?.features || [];
  }

  /**
   * Get component to feature mapping
   */
  getComponentToFeatureMapping(): Record<string, string> {
    return this.featureConfig?.componentToFeatureMapping || {};
  }
}
